import { useState, useCallback, useRef, useEffect } from "react";
import {
  Building2,
  Search,
  UserPlus,
  ChevronRight,
  ChevronLeft,
  Check,
  AlertTriangle,
  Loader2,
  Mail,
  Shield,
} from "lucide-react";
import type {
  OrganizationType,
  OrganizationTier,
  AdminCreateOrganizationInput,
  AdminUser,
} from "../types";
import { ORG_TYPES, ORG_TYPE_LABEL_KEYS, ORG_TYPE_EMOJI, ORG_TIERS, ORG_TIER_LABELS, ORG_TIER_DESCRIPTION_KEYS } from "../constants";
import { useSearchUsers } from "../hooks/useOrganizations";
import { useLocale } from "../../../context/LanguageContext";
import { MutationErrorBanner } from "../../../components/ui/MutationErrorBanner";

// ─── Step indicator ──────────────────────────────────────────────────────────

const STEPS = [
  { labelKey: "organizations.field.organization", icon: Building2 },
  { labelKey: "organizations.wizard.stepAssignAdmin", icon: UserPlus },
  { labelKey: "organizations.wizard.stepReview", icon: Check },
] as const;

/** Shared role-label i18n keys, keyed by the raw backend role enum. */
const ROLE_LABEL_KEYS: Record<string, string> = {
  ADMIN: "roles.ADMIN",
  GLOBAL_ADMIN: "roles.GLOBAL_ADMIN",
  ORG_ADMIN: "roles.ORG_ADMIN",
  USER: "roles.USER",
};

function StepIndicator({ current }: { current: number }) {
  const { t } = useLocale();
  return (
    <div className="flex items-center justify-center gap-2">
      {STEPS.map((s, i) => {
        const Icon = s.icon;
        const isCompleted = i < current;
        const isActive = i === current;
        return (
          <div key={s.labelKey} className="flex items-center gap-2">
            {i > 0 && (
              <div
                className={`h-px w-8 ${isCompleted ? "bg-primary" : "bg-border"}`}
              />
            )}
            <div className="flex items-center gap-1.5">
              <div
                className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-medium transition-colors ${
                  isCompleted
                    ? "bg-primary text-primary-foreground"
                    : isActive
                      ? "border-2 border-primary bg-primary/10 text-primary"
                      : "border border-border text-muted-foreground"
                }`}
              >
                {isCompleted ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  <Icon className="h-3.5 w-3.5" />
                )}
              </div>
              <span
                className={`text-xs font-medium ${isActive ? "text-foreground" : "text-muted-foreground"}`}
              >
                {t(s.labelKey)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Shared input styles ─────────────────────────────────────────────────────

const inputClass =
  "mt-1.5 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary";

const labelClass = "block text-sm font-medium text-foreground";

// ─── Step 1: Organization Details ────────────────────────────────────────────

function OrgDetailsStep({
  name,
  setName,
  type,
  setType,
  tier,
  setTier,
  description,
  setDescription,
  website,
  setWebsite,
}: {
  name: string;
  setName: (v: string) => void;
  type: OrganizationType;
  setType: (v: OrganizationType) => void;
  tier: OrganizationTier;
  setTier: (v: OrganizationTier) => void;
  description: string;
  setDescription: (v: string) => void;
  website: string;
  setWebsite: (v: string) => void;
}) {
  const { t } = useLocale();
  const isNameValid = name.length === 0 || (name.trim().length >= 2 && name.trim().length <= 100);

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="org-name" className={labelClass}>
          {t("organizations.field.orgName")} <span className="text-destructive">*</span>
        </label>
        <input
          id="org-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("organizations.field.namePlaceholder")}
          maxLength={100}
          className={inputClass}
          autoFocus
        />
        {!isNameValid && (
          <p className="mt-1 text-xs text-destructive">
            {t("organizations.validation.nameLength")}
          </p>
        )}
      </div>

      <div>
        <label className={labelClass}>
          {t("organizations.field.orgType")} <span className="text-destructive">*</span>
        </label>
        <div className="mt-1.5 flex gap-2">
          {ORG_TYPES.map((orgType) => (
            <button
              key={orgType}
              type="button"
              onClick={() => setType(orgType)}
              className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                type === orgType
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
            >
              <span className="mr-1.5">{ORG_TYPE_EMOJI[orgType]}</span>
              {t(ORG_TYPE_LABEL_KEYS[orgType])}
            </button>
          ))}
        </div>
      </div>

      {/* Tier selector — only for COMMERCIAL orgs */}
      {type === "COMMERCIAL" && (
        <div>
          <label className={labelClass}>
            {t("organizations.field.planTier")} <span className="text-destructive">*</span>
          </label>
          <div className="mt-1.5 flex gap-2">
            {ORG_TIERS.map((orgTier) => (
              <button
                key={orgTier}
                type="button"
                onClick={() => setTier(orgTier)}
                className={`flex-1 rounded-md border px-3 py-2 text-center transition-colors ${
                  tier === orgTier
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                <span className="block text-sm font-medium">{ORG_TIER_LABELS[orgTier]}</span>
                <span className="block text-xs text-muted-foreground">{t(ORG_TIER_DESCRIPTION_KEYS[orgTier])}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div>
        <label htmlFor="org-description" className={labelClass}>
          {t("organizations.field.description")}
        </label>
        <textarea
          id="org-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t("adminUI.organizations.createDescriptionPlaceholder")}
          rows={2}
          maxLength={2000}
          className={inputClass}
        />
      </div>

      <div>
        <label htmlFor="org-website" className={labelClass}>
          {t("organizations.field.website")}
        </label>
        <input
          id="org-website"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          placeholder="https://example.com"
          maxLength={200}
          className={inputClass}
        />
      </div>
    </div>
  );
}

// ─── Step 2: Assign ORG_ADMIN ────────────────────────────────────────────────

function AssignAdminStep({
  adminEmail,
  setAdminEmail,
  adminFirstName,
  setAdminFirstName,
  adminLastName,
  setAdminLastName,
  adminDepartment,
  setAdminDepartment,
  adminJobTitle,
  setAdminJobTitle,
  foundUser,
  setFoundUser,
}: {
  adminEmail: string;
  setAdminEmail: (v: string) => void;
  adminFirstName: string;
  setAdminFirstName: (v: string) => void;
  adminLastName: string;
  setAdminLastName: (v: string) => void;
  adminDepartment: string;
  setAdminDepartment: (v: string) => void;
  adminJobTitle: string;
  setAdminJobTitle: (v: string) => void;
  foundUser: AdminUser | null;
  setFoundUser: (u: AdminUser | null) => void;
}) {
  const { t } = useLocale();
  const [searchUsers, { loading: searching }] = useSearchUsers();
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [searched, setSearched] = useState(false);

  const handleEmailChange = useCallback(
    (value: string) => {
      setAdminEmail(value);
      setFoundUser(null);
      setSearched(false);

      // Reset all admin fields when email changes to prevent stale data
      // carrying over between new-user → existing-user switches
      setAdminFirstName("");
      setAdminLastName("");
      setAdminDepartment("");
      setAdminJobTitle("");

      if (debounceRef.current) clearTimeout(debounceRef.current);

      // Only search if email looks valid
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
        debounceRef.current = setTimeout(async () => {
          const result = await searchUsers({
            variables: { search: value.trim(), limit: 5 },
          });
          // Use edges from the connection response
          const rawData = result.data as Record<string, unknown> | undefined;
          const users = rawData?.users as { edges?: { node: AdminUser }[] } | undefined;
          const nodes: AdminUser[] = users?.edges?.map((e) => e.node) ?? [];
          const match = nodes.find(
            (u) => u.email.toLowerCase() === value.trim().toLowerCase(),
          );
          setFoundUser(match ?? null);
          setSearched(true);
          if (match) {
            setAdminFirstName(match.firstName);
            setAdminLastName(match.lastName);
          }
        }, 400);
      }
    },
    [searchUsers, setAdminEmail, setFoundUser, setAdminFirstName, setAdminLastName, setAdminDepartment, setAdminJobTitle],
  );

  // Cleanup
  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const isNewUser = searched && !foundUser;
  const isExistingUser = searched && foundUser !== null;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {t("organizations.wizard.searchHint")}
      </p>

      {/* Email search */}
      <div>
        <label htmlFor="admin-email" className={labelClass}>
          {t("organizations.field.adminEmail")} <span className="text-destructive">*</span>
        </label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            id="admin-email"
            type="email"
            value={adminEmail}
            onChange={(e) => handleEmailChange(e.target.value)}
            placeholder="admin@company.com"
            maxLength={254}
            className={`${inputClass} pl-9`}
            autoFocus
          />
          {searching && (
            <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
          )}
        </div>
      </div>

      {/* Existing user found */}
      {isExistingUser && foundUser && (
        <div className="rounded-md border border-primary/30 bg-primary/5 p-4">
          <div className="flex items-center gap-3">
            {foundUser.avatar ? (
              <img
                src={foundUser.avatar}
                alt=""
                className="h-10 w-10 rounded-full object-cover"
              />
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-sm font-medium text-primary">
                {foundUser.firstName[0]}
                {foundUser.lastName[0]}
              </div>
            )}
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">
                {foundUser.firstName} {foundUser.lastName}
              </p>
              <p className="text-xs text-muted-foreground">{foundUser.email}</p>
            </div>
            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
              <Shield className="h-3 w-3" />
              {ROLE_LABEL_KEYS[foundUser.role] ? t(ROLE_LABEL_KEYS[foundUser.role]) : foundUser.role}
            </span>
          </div>

          {foundUser.role === "ORG_ADMIN" && (
            <div className="mt-3 flex items-start gap-2 rounded-md bg-amber-500/10 p-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
              <p className="text-xs text-amber-700 dark:text-amber-400">
                {t("organizations.wizard.alreadyOrgAdmin")}
              </p>
            </div>
          )}

          <div className="mt-3 space-y-3">
            <div>
              <label htmlFor="admin-dept-existing" className={labelClass}>
                {t("organizations.field.department")} <span className="text-destructive">*</span>
              </label>
              <input
                id="admin-dept-existing"
                value={adminDepartment}
                onChange={(e) => setAdminDepartment(e.target.value)}
                placeholder={t("organizations.field.departmentPlaceholder")}
                maxLength={100}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="admin-title-existing" className={labelClass}>
                {t("organizations.field.jobTitle")}
              </label>
              <input
                id="admin-title-existing"
                value={adminJobTitle}
                onChange={(e) => setAdminJobTitle(e.target.value)}
                placeholder={t("organizations.field.jobTitlePlaceholder")}
                maxLength={100}
                className={inputClass}
              />
            </div>
          </div>
        </div>
      )}

      {/* New user — provisioning form */}
      {isNewUser && (
        <div className="rounded-md border border-border bg-muted/30 p-4">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-muted-foreground" />
            <p className="text-sm font-medium text-foreground">
              {t("organizations.wizard.newUserTitle")}
            </p>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {t("organizations.wizard.newUserHint")}
          </p>

          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="admin-first" className={labelClass}>
                {t("organizations.field.firstName")} <span className="text-destructive">*</span>
              </label>
              <input
                id="admin-first"
                value={adminFirstName}
                onChange={(e) => setAdminFirstName(e.target.value)}
                placeholder={t("adminUI.organizations.firstNamePlaceholder")}
                maxLength={50}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="admin-last" className={labelClass}>
                {t("organizations.field.lastName")} <span className="text-destructive">*</span>
              </label>
              <input
                id="admin-last"
                value={adminLastName}
                onChange={(e) => setAdminLastName(e.target.value)}
                placeholder={t("adminUI.organizations.lastNamePlaceholder")}
                maxLength={50}
                className={inputClass}
              />
            </div>
          </div>

          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="admin-dept" className={labelClass}>
                {t("organizations.field.department")} <span className="text-destructive">*</span>
              </label>
              <input
                id="admin-dept"
                value={adminDepartment}
                onChange={(e) => setAdminDepartment(e.target.value)}
                placeholder={t("organizations.field.departmentPlaceholder")}
                maxLength={100}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="admin-title" className={labelClass}>
                {t("organizations.field.jobTitle")}
              </label>
              <input
                id="admin-title"
                value={adminJobTitle}
                onChange={(e) => setAdminJobTitle(e.target.value)}
                placeholder={t("organizations.field.jobTitlePlaceholder")}
                maxLength={100}
                className={inputClass}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Step 3: Review & Confirm ────────────────────────────────────────────────

function ReviewStep({
  input,
  foundUser,
}: {
  input: AdminCreateOrganizationInput;
  foundUser: AdminUser | null;
}) {
  const { t } = useLocale();
  const isNewUser = !foundUser;
  return (
    <div className="space-y-5">
      {/* Org summary */}
      <div>
        <h4 className="text-sm font-semibold text-foreground">{t("organizations.field.organization")}</h4>
        <div className="mt-2 rounded-md border border-border bg-muted/30 p-3 text-sm">
          <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
            <span className="text-muted-foreground">{t("organizations.field.name")}</span>
            <span className="font-medium text-foreground">{input.name}</span>
            <span className="text-muted-foreground">{t("organizations.field.type")}</span>
            <span className="text-foreground">
              {ORG_TYPE_EMOJI[input.type]} {t(ORG_TYPE_LABEL_KEYS[input.type])}
            </span>
            {input.tier && (
              <>
                <span className="text-muted-foreground">{t("organizations.field.tier")}</span>
                <span className="text-foreground">
                  {ORG_TIER_LABELS[input.tier]}
                  <span className="ml-1.5 text-xs text-muted-foreground">
                    ({t(ORG_TIER_DESCRIPTION_KEYS[input.tier])})
                  </span>
                </span>
              </>
            )}
            {input.description && (
              <>
                <span className="text-muted-foreground">{t("organizations.field.description")}</span>
                <span className="text-foreground">{input.description}</span>
              </>
            )}
            {input.website && (
              <>
                <span className="text-muted-foreground">{t("organizations.field.website")}</span>
                <span className="text-foreground">{input.website}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Admin summary */}
      <div>
        <h4 className="text-sm font-semibold text-foreground">
          {t("organizations.wizard.adminHeading")}
        </h4>
        <div className="mt-2 rounded-md border border-border bg-muted/30 p-3 text-sm">
          <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
            <span className="text-muted-foreground">{t("organizations.field.email")}</span>
            <span className="font-medium text-foreground">
              {input.adminEmail}
            </span>
            <span className="text-muted-foreground">{t("organizations.field.name")}</span>
            <span className="text-foreground">
              {input.adminFirstName} {input.adminLastName}
            </span>
            <span className="text-muted-foreground">{t("organizations.field.department")}</span>
            <span className="text-foreground">{input.adminDepartment}</span>
            {input.adminJobTitle && (
              <>
                <span className="text-muted-foreground">{t("organizations.field.jobTitle")}</span>
                <span className="text-foreground">{input.adminJobTitle}</span>
              </>
            )}
            <span className="text-muted-foreground">{t("organizations.field.status")}</span>
            <span className="text-foreground">
              {isNewUser ? (
                <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                  <UserPlus className="h-3.5 w-3.5" />
                  {t("organizations.wizard.statusNewAccount")}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                  <Check className="h-3.5 w-3.5" />
                  {t("organizations.wizard.statusExistingUser")}
                </span>
              )}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Dialog ─────────────────────────────────────────────────────────────

interface CreateOrganizationDialogProps {
  onConfirm: (input: AdminCreateOrganizationInput) => void;
  onCancel: () => void;
  loading: boolean;
  error?: string | null;
}

export function CreateOrganizationDialog({
  onConfirm,
  onCancel,
  loading,
  error,
}: CreateOrganizationDialogProps) {
  const { t } = useLocale();
  const [step, setStep] = useState(0);

  // Step 1 — Org details
  const [name, setName] = useState("");
  const [type, setType] = useState<OrganizationType>("COMMERCIAL");
  const [tier, setTier] = useState<OrganizationTier>("SEED");
  const [description, setDescription] = useState("");
  const [website, setWebsite] = useState("");

  // Step 2 — Admin assignment
  const [adminEmail, setAdminEmail] = useState("");
  const [adminFirstName, setAdminFirstName] = useState("");
  const [adminLastName, setAdminLastName] = useState("");
  const [adminDepartment, setAdminDepartment] = useState("");
  const [adminJobTitle, setAdminJobTitle] = useState("");
  const [foundUser, setFoundUser] = useState<AdminUser | null>(null);

  // ─── Validation ──────────────────────────────────────────────────────
  const isStep1Valid =
    name.trim().length >= 2 && name.trim().length <= 100;

  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adminEmail.trim());
  const isStep2Valid =
    isEmailValid &&
    adminFirstName.trim().length >= 1 &&
    adminLastName.trim().length >= 1 &&
    adminDepartment.trim().length >= 1;

  const canGoNext = step === 0 ? isStep1Valid : step === 1 ? isStep2Valid : true;

  const buildInput = (): AdminCreateOrganizationInput => ({
    name: name.trim(),
    type,
    ...(type === "COMMERCIAL" ? { tier } : {}),
    description: description.trim() || undefined,
    website: website.trim() || undefined,
    adminEmail: adminEmail.trim(),
    adminFirstName: adminFirstName.trim(),
    adminLastName: adminLastName.trim(),
    adminDepartment: adminDepartment.trim(),
    adminJobTitle: adminJobTitle.trim() || undefined,
  });

  const handleNext = () => {
    if (step < 2 && canGoNext) setStep(step + 1);
  };
  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };
  const handleSubmit = () => {
    if (!canGoNext || loading) return;
    onConfirm(buildInput());
  };

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/40" onClick={onCancel} />
      <div className="fixed left-1/2 top-1/2 z-50 w-full max-w-xl -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-card shadow-xl">
        {/* Header */}
        <div className="border-b border-border px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-primary/10 p-2">
              <Building2 className="h-5 w-5 text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">
              {t("organizations.create.title")}
            </h3>
          </div>
          <div className="mt-4">
            <StepIndicator current={step} />
          </div>
        </div>

        {/* Body */}
        <div className="max-h-[60vh] overflow-y-auto px-6 py-5">
          {step === 0 && (
            <OrgDetailsStep
              name={name}
              setName={setName}
              type={type}
              setType={setType}
              tier={tier}
              setTier={setTier}
              description={description}
              setDescription={setDescription}
              website={website}
              setWebsite={setWebsite}
            />
          )}
          {step === 1 && (
            <AssignAdminStep
              adminEmail={adminEmail}
              setAdminEmail={setAdminEmail}
              adminFirstName={adminFirstName}
              setAdminFirstName={setAdminFirstName}
              adminLastName={adminLastName}
              setAdminLastName={setAdminLastName}
              adminDepartment={adminDepartment}
              setAdminDepartment={setAdminDepartment}
              adminJobTitle={adminJobTitle}
              setAdminJobTitle={setAdminJobTitle}
              foundUser={foundUser}
              setFoundUser={setFoundUser}
            />
          )}
          {step === 2 && (
            <ReviewStep input={buildInput()} foundUser={foundUser} />
          )}
        </div>

        {/* Error banner — rendered above the footer so it's adjacent to the submit button */}
        {error && (
          <div className="border-t border-border px-6 pt-4">
            <MutationErrorBanner message={error} />
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-border px-6 py-4">
          <button
            type="button"
            onClick={step === 0 ? onCancel : handleBack}
            className="inline-flex h-9 items-center gap-1.5 rounded-md px-4 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            {step === 0 ? (
              t("common.cancel")
            ) : (
              <>
                <ChevronLeft className="h-4 w-4" />
                {t("common.back")}
              </>
            )}
          </button>

          {step < 2 ? (
            <button
              type="button"
              onClick={handleNext}
              disabled={!canGoNext}
              className="inline-flex h-9 items-center gap-1.5 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t("common.next")}
              <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="inline-flex h-9 items-center gap-1.5 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t("organizations.create.submitting")}
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  {t("organizations.create.title")}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </>
  );
}
